import { FC, PropsWithChildren, useMemo } from "react";
import {
  AuthScopeBase,
  BrandDifferentiatedAuthScope,
  CrudOp,
  DifferentiationType,
  ProjectDifferentiatedAuthScope,
  UndifferentiatedAuthScope,
} from "../helpers/authHelperTypes";
import { useAuthHelper } from "../helpers/useAuthHelper";
import { Unauthorized } from "./OidcStateComponents";

export type AuthGuardBaseProps = PropsWithChildren<{
  authScope: AuthScopeBase;
  crudOp: CrudOp;
}>;

export type BrandBasedAuthGuardProps = AuthGuardBaseProps & {
  authScope: BrandDifferentiatedAuthScope;
  brand: string;
};

export type ProjectBasedAuthGuardProps = AuthGuardBaseProps & {
  authScope: ProjectDifferentiatedAuthScope;
  project: string;
};

export type UnspecificAuthGuardProps = AuthGuardBaseProps & {
  authScope: UndifferentiatedAuthScope;
};

export const AuthGuard: FC<BrandBasedAuthGuardProps | ProjectBasedAuthGuardProps | UnspecificAuthGuardProps> = (props) => {
  const element = useMemo(
    () => {
      switch (props.authScope.type) {
        case DifferentiationType.BRAND:
          return <BrandBasedAuthGuard {...props as BrandBasedAuthGuardProps}/>;
        case DifferentiationType.PROJECT:
          return <ProjectBasedAuthGuard {...props as ProjectBasedAuthGuardProps}/>;
        case DifferentiationType.NONE:
          return <UnspecificAuthGuard {...props as UnspecificAuthGuardProps}/>;
      }
    },
    [props],
  );

  return <>{element}</>;
};

const BrandBasedAuthGuard: FC<BrandBasedAuthGuardProps> = ({ children, authScope, crudOp, brand }) => {
  const authHelper = useAuthHelper(authScope);
  const isAuthorized = useMemo(
    () => authHelper.evaluate(crudOp, brand),
    [authHelper, crudOp, brand],
  );

  return isAuthorized ? <>{children}</> : <Unauthorized/>;
};

const ProjectBasedAuthGuard: FC<ProjectBasedAuthGuardProps> = ({ children, authScope, crudOp, project }) => {
  const authHelper = useAuthHelper(authScope);
  const isAuthorized = useMemo(
    () => authHelper.evaluate(crudOp, project),
    [authHelper, crudOp, project],
  );

  return isAuthorized ? <>{children}</> : <Unauthorized/>;
};

const UnspecificAuthGuard: FC<UnspecificAuthGuardProps> = ({ children, authScope, crudOp }) => {
  const authHelper = useAuthHelper(authScope);
  const isAuthorized = useMemo(
    () => authHelper.evaluate(crudOp),
    [authHelper, crudOp],
  );

  return isAuthorized ? <>{children}</> : <Unauthorized/>;
};
